import React, { useEffect, useRef } from 'react';

import OptionsList from '@decisiv/ui-components/lib/atoms/OptionsList';

/**
 * KeyDS OptionsList with its label clicks made to count once.
 *
 * OptionsList puts the row's click handler on the <li> and then wraps the row's
 * contents — checkbox included — in a <label>. So a click on the row's text
 * reaches the <li> twice: once as itself, and once as the click the <label>
 * forwards to the checkbox it labels. Two toggles net to nothing, which is why
 * only the checkbox itself appeared to work while the label looked dead
 * (reported in QA on PD-4063).
 *
 * Cancelling the click suppresses the forwarded one, since a <label> does not
 * activate its control for an event whose default was prevented. One click then
 * reaches the <li>. Nothing is lost by cancelling: OptionsList's checkbox is
 * controlled, handed `onChange={noop}` and driven entirely by `selected`.
 *
 * The checkbox itself is the exception, and it must stay one. KeyDS BooleanInput
 * gives the mark's own <label> an `onClick` that calls `stopPropagation`, so a
 * click there never reaches the row through React at all — the forwarded click
 * is the only way it counts. Cancelling that one turned the checkbox dead
 * instead, which is the state the first cut of this fix left it in.
 *
 * A native capture listener rather than onClickCapture, because React 16
 * delegates from the document and this has to cancel the event before the
 * browser runs the label's activation behaviour.
 */
function SelectableOptionsList(props) {
  const guardRef = useRef(null);

  useEffect(() => {
    const node = guardRef.current;
    if (!node) return undefined;

    const cancelLabelActivation = (event) => {
      const { target } = event;

      // Only the option rows: the footer's buttons and any input above the list
      // behave as they always did.
      if (!target.closest('li[data-id]')) return;

      // The checkbox and its own label rely on the forwarded click; see above.
      if (target.closest('label[for]') || target.tagName === 'INPUT') return;

      event.preventDefault();
    };

    node.addEventListener('click', cancelLabelActivation, true);
    return () => node.removeEventListener('click', cancelLabelActivation, true);
  }, []);

  // display: contents so the wrapper takes part in no layout: both callers size
  // the list against the panel around it.
  return (
    <div ref={guardRef} style={{ display: 'contents' }}>
      <OptionsList {...props} />
    </div>
  );
}

export default SelectableOptionsList;
